/** @jsx jsx */
import { Box, Flex, Grid, Heading, HeadingSize } from "@eworkit/iris";
import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { CollapsibleArea, CollapsibleButton, useCollapsible } from "@/components/collapsible";
import { UserContext } from "@/components/User";
import { TimeReport, TimeReportProjectSummary } from "@/data/time-reports";
import { useFormats } from "@/hooks/Formats";

import { TimeReportSummaryTable } from "./_components/TimeReportSummaryTable";

interface TimeReportSummaryProjectProps {
  timeReport: TimeReport;
  project: TimeReportProjectSummary;
  collapsible?: boolean;
  headingSize?: HeadingSize;
}

export const TimeReportSummaryProject: FC<TimeReportSummaryProjectProps> = ({
  timeReport,
  project,
  collapsible,
  headingSize = "h-sm",
}) => {
  const { t } = useTranslation("session");
  const { hasCompanyManagerPrivileges } = useContext(UserContext);
  const { formatCost } = useFormats();

  const collapsibleState = useCollapsible(Boolean(collapsible));

  const hourlyItems = project.items.filter(i => i.payCode.measureUnit.code === "HOURLY");
  const dailyItems = project.items.filter(i => i.payCode.measureUnit.code === "DAILY");

  const totalAmount = project.items.reduce((acc, v) => acc + v.cost, 0);

  return (
    <Grid gap="sm">
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size={headingSize} sx={{ lineHeight: "h-lg" }}>
          {project.project?.projectName || t("TIME_REPORTS.SUMMARY.PROJECTS.HEADER")}
        </Heading>
        {collapsible && <CollapsibleButton {...collapsibleState} />}
      </Flex>

      <CollapsibleArea {...collapsibleState}>
        <Flex flexDirection="column" gap={["xl", null, "sm"]}>
          {hourlyItems.length > 0 && (
            <TimeReportSummaryTable variant="hourly" timeReport={timeReport} items={hourlyItems} />
          )}

          {dailyItems.length > 0 && (
            <TimeReportSummaryTable variant="daily" timeReport={timeReport} items={dailyItems} />
          )}

          {hasCompanyManagerPrivileges() && (
            <Box py={[null, null, "sm"]}>
              <Heading size={"h-xs"} bold align={["left", null, "right"]}>
                {t("TIME_REPORTS.SUMMARY.PROJECTS.TOTAL_AMOUNT", {
                  value: formatCost(totalAmount, timeReport.currency.code, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                })}
              </Heading>
            </Box>
          )}
        </Flex>
      </CollapsibleArea>
    </Grid>
  );
};
